const MS_PER_DAY = 86400000;

export function formatRelativeTimestamp(
    input: string | number | Date,
    now: Date = new Date(),
): string {
    const date = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(date.getTime())) return "";

    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / MS_PER_DAY);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
}

/** Whole days (24 h periods) elapsed since `input`, floored at 0; null when
 *  absent or unparsable. Same day arithmetic as formatRelativeTimestamp above
 *  so the two never disagree about "N days". */
export function daysSince(input: string | null, now: Date = new Date()): number | null {
    if (!input) return null;
    const date = new Date(input);
    if (Number.isNaN(date.getTime())) return null;
    return Math.max(0, Math.floor((now.getTime() - date.getTime()) / MS_PER_DAY));
}

/** Year, month and day of a `YYYY-MM-DD` date, or the viewer's local day of a full ISO timestamp (a reviewer's `due_at`). */
function dayParts(iso: string): [number, number, number] {
    if (iso.length > 10) {
        const at = new Date(iso);
        if (!Number.isNaN(at.getTime())) return [at.getFullYear(), at.getMonth() + 1, at.getDate()];
    }
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    return [y, m, d];
}

/** Whole days from `today` to an ISO `YYYY-MM-DD` date. Negative means past. Local-time midday arithmetic so DST never shifts a day. */
export function daysUntil(iso: string, today: Date = new Date()): number {
    const [y, m, d] = dayParts(iso);
    const target = new Date(y, m - 1, d, 12);
    const base = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12);
    return Math.round((target.getTime() - base.getTime()) / MS_PER_DAY);
}

// One formatter for every short date: `toLocaleDateString` with options builds a new one per call
// (about 35 µs, 100 times this), and a register draws one or two dates on each of its rows.
const SHORT_DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

/** "Sep 18", with the year only when it is not `today`'s year. For due and sent dates in registers and rails. */
export function shortDate(iso: string, today: Date = new Date()): string {
    const [y, m, d] = dayParts(iso);
    const date = new Date(y, m - 1, d, 12);
    const text = SHORT_DAY.format(date);
    return y === today.getFullYear() ? text : `${text}, ${y}`;
}

// One formatter per named zone and zone-name style, for the same reason as SHORT_DAY:
// projectDateTime labels every row of a register in its project's zone. A call without a zone
// builds its own, so the viewer's zone is read when it is drawn, never pinned by a first call.
// Kept in insertion order and capped: a page names a handful of zones.
const ZONED_INSTANTS = new Map<string, Intl.DateTimeFormat>();
const MAX_ZONED_INSTANTS = 32;

function instantFormatter(
    timeZone: string | undefined,
    timeZoneName: Intl.DateTimeFormatOptions["timeZoneName"],
): Intl.DateTimeFormat {
    const key = timeZone === undefined ? undefined : `${timeZone}|${timeZoneName ?? ""}`;
    const kept = key === undefined ? undefined : ZONED_INSTANTS.get(key);
    if (kept) return kept;
    // An unknown zone throws here, before anything is kept.
    const formatter = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone,
        timeZoneName,
    });
    if (key !== undefined) {
        ZONED_INSTANTS.set(key, formatter);
        if (ZONED_INSTANTS.size > MAX_ZONED_INSTANTS)
            ZONED_INSTANTS.delete(ZONED_INSTANTS.keys().next().value as string);
    }
    return formatter;
}

/** An ISO instant in `timeZone` (the viewer's when omitted). Project-only displays may name
 *  the zone and supply an unavailable label instead of falling back to the viewer's zone. */
export function instantLabel(
    iso: string | null | undefined,
    timeZone?: string,
    {
        timeZoneName,
        invalidZone,
    }: Pick<Intl.DateTimeFormatOptions, "timeZoneName"> & {
        invalidZone?: string;
    } = {},
): string {
    if (!iso) return "";
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return iso;
    try {
        return instantFormatter(timeZone, timeZoneName).format(at);
    } catch (error) {
        if (!(error instanceof RangeError)) throw error;
        return invalidZone ?? instantFormatter(undefined, timeZoneName).format(at);
    }
}

/** `timeZone` when `Intl` knows the name, else `fallback`: an unknown zone never throws in render. */
export function knownTimeZone(timeZone: string, fallback: string): string {
    try {
        new Intl.DateTimeFormat("en-US", { timeZone });
        return timeZone;
    } catch {
        return fallback;
    }
}

const zonedDay = (at: Date, timeZone: string | undefined) =>
    at.toLocaleDateString("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" });

const zonedHour = (at: Date, timeZone: string | undefined) =>
    Number(
        at.toLocaleTimeString("en-GB", { hour: "2-digit", hour12: false, timeZone }).slice(0, 2),
    );

/**
 * A moment as a project's pages say it, in `timeZone` (a project's zone; the viewer's when
 * omitted): "8:17 AM" today ("Today 8:17 AM" with `todayPrefix`, for a column of moments),
 * "Yesterday 11:27 PM", "Tonight 11:20 PM" / "Tomorrow 1:00 AM" ahead, "Sep 10, 8:17 AM"
 * otherwise; "—" when there is none. An unknown zone reads as UTC.
 */
export function zonedWhen(
    iso: string | null | undefined,
    timeZone?: string,
    now: Date = new Date(),
    { todayPrefix = false }: { todayPrefix?: boolean } = {},
): string {
    if (!iso) return "—";
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return "—";
    const zone = timeZone === undefined ? undefined : knownTimeZone(timeZone, "UTC");
    const time = at.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: zone,
    });
    const today = zonedDay(now, zone);
    const day = zonedDay(at, zone);
    if (day === today) {
        if (at > now && zonedHour(at, zone) >= 18) return `Tonight ${time}`;
        return todayPrefix ? `Today ${time}` : time;
    }
    const step = (days: number) => zonedDay(new Date(now.getTime() + days * MS_PER_DAY), zone);
    if (day === step(-1)) return `Yesterday ${time}`;
    if (day === step(1)) return `Tomorrow ${time}`;
    const date = at.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: zone });
    return `${date}, ${time}`;
}

/** "May 21, 2026": the day of an instant in `timeZone`. An unknown zone reads as UTC. */
export function zonedDate(iso: string, timeZone: string): string {
    return new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: knownTimeZone(timeZone, "UTC"),
    });
}

/** "Eastern Time" for a "Times in …" note. An unknown zone reads as UTC. */
export function zoneName(timeZone: string): string {
    const zone = knownTimeZone(timeZone, "UTC");
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "long" })
        .formatToParts(new Date())
        .find((p) => p.type === "timeZoneName");
    return (part?.value ?? zone).replace(/ (Standard|Daylight) /, " ");
}

/**
 * "Sep 20, 7:37 AM" for an activity or comment timestamp: the day and the minute, the year
 * only when it is not `now`'s. Callers put the full ISO instant in a `title` for the hover.
 * The raw text comes back when it does not parse.
 */
export function formatActivityTime(iso: string | null | undefined, now: Date = new Date()): string {
    if (!iso) return "";
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return iso;
    const day = at.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        ...(at.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
    });
    const time = at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${day}, ${time}`;
}
